import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { resolveApiUrl } from '../api/apiClient';
import { getAuth0TokenAuthorizationParams } from '../auth/auth0TokenParams';
import {
  NOTIFICATION_STREAM_PATH,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationsRead,
} from '../services/qc/notificationService';
import { isQcLiteE2eBypass } from '../utils/qcLiteE2eBypass';

export const NOTIFICATION_KEYS = {
  all: ['notifications'],
  list: ['notifications', 'list'],
  unread: ['notifications', 'unread'],
};

const PAGE_SIZE = 20;
/** Unread count polling while the live stream is down */
const FALLBACK_POLL_MS = 60 * 1000;
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 60 * 1000;

/** Auth failures: stop reconnecting (polling still runs, and re-login fixes it). */
class FatalStreamError extends Error {}

function mapListItems(data, fn) {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: page.items.map(fn) })),
  };
}

/**
 * Keeps the server's SSE stream open while the tab is visible and folds
 * pushed notifications into the React Query cache. The REST endpoints stay
 * the source of truth: the inbox is refetched on every (re)connect and on
 * 'resync'. Returns whether the stream is currently live.
 */
function useNotificationStream(enabled) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    let attempt = 0;

    const refetchAll = () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });

    fetchEventSource(resolveApiUrl(NOTIFICATION_STREAM_PATH), {
      signal: ctrl.signal,
      // Closes while the tab is hidden and reopens (→ onopen → refetch) on return
      openWhenHidden: false,
      async fetch(input, init) {
        const token = await getAccessTokenSilently({
          authorizationParams: getAuth0TokenAuthorizationParams(),
        });
        return fetch(input, {
          ...init,
          headers: { ...init?.headers, Authorization: `Bearer ${token}` },
        });
      },
      async onopen(res) {
        const type = res.headers.get('content-type') ?? '';
        if (res.ok && type.startsWith('text/event-stream')) {
          attempt = 0;
          setLive(true);
          refetchAll();
          return;
        }
        if (res.status === 401 || res.status === 403) {
          throw new FatalStreamError(`stream ${res.status}`);
        }
        throw new Error(`stream ${res.status}`);
      },
      onmessage(ev) {
        if (ev.event === 'resync') {
          refetchAll();
          return;
        }
        if (ev.event !== 'notification' || !ev.data) return;
        let notification;
        try {
          notification = JSON.parse(ev.data);
        } catch {
          return;
        }
        queryClient.setQueryData(NOTIFICATION_KEYS.list, (data) => {
          if (!data?.pages?.length) return data;
          const [first, ...rest] = data.pages;
          if (
            first.items.some(
              (n) => n.notification_id === notification.notification_id,
            )
          ) {
            return data;
          }
          return {
            ...data,
            pages: [{ ...first, items: [notification, ...first.items] }, ...rest],
          };
        });
        queryClient.setQueryData(NOTIFICATION_KEYS.unread, (c) => (c ?? 0) + 1);
      },
      onclose() {
        // Server ended the stream (e.g. deploy restart): reconnect
        setLive(false);
        throw new Error('stream closed');
      },
      onerror(err) {
        setLive(false);
        if (err instanceof FatalStreamError) throw err;
        attempt += 1;
        return Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
      },
    }).catch(() => {
      // Fatal (auth) or aborted: stay on polling
      setLive(false);
    });

    return () => {
      ctrl.abort();
      setLive(false);
    };
  }, [enabled, getAccessTokenSilently, queryClient]);

  return live;
}

/**
 * Notification bell state: unread count (live via SSE, polled as fallback),
 * the paged inbox (fetched when `listEnabled`), and read mutations with
 * optimistic updates. Nothing is marked read just by opening the list.
 */
export function useNotifications({ listEnabled = false } = {}) {
  const queryClient = useQueryClient();
  // E2E bypass runs against mocked routes; skip the long-lived stream there
  const streamEnabled = !isQcLiteE2eBypass();
  const live = useNotificationStream(streamEnabled);

  const unreadQuery = useQuery({
    queryKey: NOTIFICATION_KEYS.unread,
    queryFn: getUnreadNotificationCount,
    staleTime: 30 * 1000,
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });

  const listQuery = useInfiniteQuery({
    queryKey: NOTIFICATION_KEYS.list,
    queryFn: ({ pageParam }) =>
      getNotifications({ beforeId: pageParam, limit: PAGE_SIZE }),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextBeforeId ?? undefined,
    enabled: listEnabled,
    staleTime: 30 * 1000,
  });

  const { mutate: markReadMutate } = useMutation({
    mutationFn: (ids) => markNotificationsRead(ids),
    onMutate: (ids) => {
      const now = new Date().toISOString();
      let newlyRead = 0;
      queryClient.setQueryData(NOTIFICATION_KEYS.list, (data) =>
        mapListItems(data, (n) => {
          if (!ids.includes(n.notification_id) || n.read_at) return n;
          newlyRead += 1;
          return { ...n, read_at: now };
        }),
      );
      queryClient.setQueryData(NOTIFICATION_KEYS.unread, (c) =>
        Math.max(0, (c ?? 0) - newlyRead),
      );
    },
    onError: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });

  const { mutate: markAllReadMutate } = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => {
      const now = new Date().toISOString();
      queryClient.setQueryData(NOTIFICATION_KEYS.list, (data) =>
        mapListItems(data, (n) => (n.read_at ? n : { ...n, read_at: now })),
      );
      queryClient.setQueryData(NOTIFICATION_KEYS.unread, 0);
    },
    onError: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });

  const items = listQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return {
    unreadCount: unreadQuery.data ?? 0,
    items,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    hasMore: Boolean(listQuery.hasNextPage),
    loadMore: listQuery.fetchNextPage,
    isLoadingMore: listQuery.isFetchingNextPage,
    live,
    /** @param {number} id */
    markRead: (id) => markReadMutate([id]),
    markAllRead: () => markAllReadMutate(),
  };
}
